import { findNodeHandle, AccessibilityInfo, Platform, NativeModules } from 'react-native';

const { AccessibilityModule } = NativeModules;

export interface FocusManagerOptions {
    /** Set accessibility focus for screen readers (TalkBack/VoiceOver) */
    setAccessibilityFocus?: boolean;
    /** Set keyboard focus for physical keyboards (requires native module on Android) */
    setKeyboardFocus?: boolean;
    /** Delay before setting focus (ms) */
    delay?: number;
}

/**
 * Sets both accessibility focus (for screen readers) and keyboard focus
 * (for physical keyboards) on a target element.
 *
 * This solves the WCAG 2.4.3 Focus Order issue where React Native's
 * AccessibilityInfo.setAccessibilityFocus() only works for screen readers
 * (TalkBack/VoiceOver) but doesn't move keyboard focus.
 *
 * ## Use Cases:
 * - When a focused element is removed from DOM (e.g., hiding a job card)
 * - After navigation returns to a previous screen
 * - When showing modal/overlay content
 *
 * ## Example:
 * ```typescript
 * const undoButtonRef = useRef<Pressable>(null);
 *
 * useEffect(() => {
 *   if (shouldFocusButton) {
 *     setUniversalFocus(undoButtonRef);
 *   }
 * }, [shouldFocusButton]);
 * ```
 *
 * @param targetRef - React ref to the target element
 * @param options - Configuration options
 */
export function setUniversalFocus(
    targetRef: React.RefObject<any>,
    options: FocusManagerOptions = {},
): () => void {
    // findNodeHandle is not supported on web — accessibility focus is handled natively by the browser
    if (Platform.OS === 'web') {
        return () => {};
    }

    const { setAccessibilityFocus = true, setKeyboardFocus = true, delay = 100 } = options;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const reactTag = findNodeHandle(targetRef.current);
    if (!reactTag) {
        console.warn('[AccessibilityFocusManager] Unable to find node handle for target ref');
        return () => { };
    }

    const timer = setTimeout(() => {
        // Set accessibility focus for screen readers (TalkBack/VoiceOver)
        if (setAccessibilityFocus) {
            AccessibilityInfo.setAccessibilityFocus(reactTag);
        }

        // Set keyboard focus for physical keyboards (Android only, requires native module)
        if (setKeyboardFocus && Platform.OS === 'android') {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (AccessibilityModule && typeof AccessibilityModule.requestFocus === 'function') {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                AccessibilityModule.requestFocus(reactTag);
            } else {
                console.warn(
                    '[AccessibilityFocusManager] Native AccessibilityModule not available. ' +
                    'Keyboard focus will not be set. This is expected on iOS or if the ' +
                    'native module is not registered.',
                );
            }
        }
    }, delay);

    return () => clearTimeout(timer);
}
